import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { AdminNavigationComponent } from "../../components/admin-navigation/admin-navigation.component";
import { Router } from '@angular/router';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { AdminChangesSendEmail, InvoiceElectricityAndCompanyGet, InvoiceStatus, ReviewUpdate, VisibilityStatus } from '../../../../core/interfaces/invoice-interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { IsoToFormattedDateTimePipe } from "../../../../core/pipes/iso-to-formatted-date-time.pipe";
import { FilesService } from '../../../../core/api/files.service';
import { lastValueFrom, combineLatest, Observable } from 'rxjs';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { AdminReviewExpandedElectricityInvoiceComponent } from "../../components/admin-review-expanded-electricity-invoice/admin-review-expanded-electricity-invoice.component";
import { LocationService } from '../../../../core/api/location.service';
import { Location } from '../../../../core/interfaces/location';
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { FormsModule } from '@angular/forms';
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";
import { DeleteConfirmationDialogComponent } from '../../../../shared/dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { UtilsService } from '../../../../core/services/utils.service';
import { UserDataService } from '../../../../core/services/user-data.service';
import { AdminInvoiceStatusTextComponent } from '../../components/admin-invoice-status-text/admin-invoice-status-text.component';
import { InvoiceHelperService } from '../../../../core/services/invoice-helper.service';

@Component({
  selector: 'app-admin-electricity-invoice-check-review-page',
  standalone: true,
  imports: [ToastComponent, AdminNavigationComponent, TranslocoPipe, CommonModule, IsoToFormattedDateTimePipe, CustomButtonComponent, AdminReviewExpandedElectricityInvoiceComponent, CustomDropdownComponent, FormsModule, AdminTopbarComponent, DeleteConfirmationDialogComponent, AdminInvoiceStatusTextComponent],
  templateUrl: './admin-electricity-invoice-check-review-page.component.html',
  styleUrl: './admin-electricity-invoice-check-review-page.component.css'
})
export class AdminElectricityInvoiceCheckReviewPageComponent implements OnInit {
  constructor(private router: Router, private invoiceService: InvoiceService, private apiError: ApiErrorService, private filesService: FilesService,
    private locationService: LocationService,
    private translocoService: TranslocoService,
    private dateConversion: DateConversionService,
    private utilsService: UtilsService,
    private userData: UserDataService,
    private cdr: ChangeDetectorRef,
    private invoiceHelperService: InvoiceHelperService
  ) { }

  invoices: InvoiceElectricityAndCompanyGet[] = [];
  loadedInvoices: InvoiceElectricityAndCompanyGet[] = [];

  lastLoadedPageNumber: number = 0;
  loadMoreHidden = false;

  filterDropdownOptions: string[] = [
    'Filter',
    'By Date (ASC)',
    'By Date (DESC)',
    'By Client (ASC)',
    'By Client (DESC)',
    'By Supplier (ASC)',
    'By Supplier (DESC)',
  ];

  selectedFilterDropdown: number = 0;

  statusDropdownOptions: string[] = [
    'Show For Review',
    'Show Reviewed',
    'Show All'
  ];

  selectedStatusDropdown: number = 0;

  // Search filter
  searchTerm: string = '';

  // Expanded Row Id
  expandedRow: number | null = null;
  loadingExpandedRow: boolean = false;

  expandedFileUrl?: string;
  expandedFileId?: string;

  // Locations for expanded row
  listOfLocations: Location[] = [];

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Approve invoice
  changingStatus: boolean = false;
  @ViewChild(DeleteConfirmationDialogComponent) confirmStatusUpdate!: DeleteConfirmationDialogComponent;
  confirmDialogTitle = 'admin.finalize_show';
  confirmDialogBody = 'admin.finalize_show_desc';

  // Expanded row
  @ViewChild('expandedRow') expandedRowElement?: AdminReviewExpandedElectricityInvoiceComponent;

  ngOnInit(): void {
    const filterKeys = [
      'invoice.filters.default',
      'invoice.filters.date_asc',
      'invoice.filters.date_desc',
      'invoice.filters.client_asc',
      'invoice.filters.client_desc',
      'invoice.filters.supplier_asc',
      'invoice.filters.supplier_desc',
    ];

    const statusSortKeys = [
      'invoice.filters.show_needs_review_only',
      'invoice.filters.show_reviewed_only',
      'invoice.filters.show_all',
    ];

    combineLatest(filterKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterDropdownOptions = translations;
    });

    combineLatest(statusSortKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.statusDropdownOptions = translations;
    });

    this.loadInvoicesByStatus(0);
  }

  goToDashboard() {
    this.router.navigate(["/dashboard"]);
  }

  async toggleDetails(index: number, open?: boolean): Promise<void> {
    if (this.loadingExpandedRow && !this.invoices[index]) {
      return;
    }

    this.loadingExpandedRow = true;


    // Get file url
    this.expandedFileUrl = await this.getFileUrl(this.invoices[index].file.id) ?? undefined;
    this.expandedFileId = this.invoices[index].file.id ?? undefined;

    // Load company locations for dropdown
    await this.reloadLocations(index);

    this.loadingExpandedRow = false;

    if (open == undefined) {
      this.expandedRow = this.expandedRow === index ? null : index;
    }
    else {
      this.expandedRow = open ? index : null;
    }
    this.cdr.detectChanges();
  }

  loadInvoicesByStatus(statusIndex: number) {
    this.lastLoadedPageNumber = 0;
    this.loadedInvoices = [];
    this.loadMoreHidden = false;

    if (statusIndex === 0) {
      this.loadElectricityInvoicesNewPage(() => this.invoiceService.getElectricityReviewInvoices(this.lastLoadedPageNumber + 1));
    } else if (statusIndex === 1) {
      this.loadElectricityInvoicesNewPage(() => this.invoiceService.getReviewedOnlyElectricityReviewInvoices(this.lastLoadedPageNumber + 1));
    } else if (statusIndex === 2) {
      this.loadElectricityInvoicesNewPage(() => this.invoiceService.getAllElectricityReviewInvoices(this.lastLoadedPageNumber + 1));
    }
  }

  private loadElectricityInvoicesNewPage(apiCall: () => Observable<any>) {
    apiCall().subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.loadedInvoices.length + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }

          this.loadedInvoices = [...this.loadedInvoices, ...response.data];

          // Applay filter
          this.resetInvoicesFilter();
          this.applySearchFilter();
          this.applySelectedFilter();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumber++;
      }
    });
  }

  loadMoreInvoices = () => {
    this.loadElectricityInvoicesNewPage(this.getCurrentApiCall());
  };

  private getCurrentApiCall() {
    if (this.selectedStatusDropdown === 0) {
      return () => this.invoiceService.getElectricityReviewInvoices(this.lastLoadedPageNumber + 1);
    } else if (this.selectedStatusDropdown === 1) {
      return () => this.invoiceService.getReviewedOnlyElectricityReviewInvoices(this.lastLoadedPageNumber + 1);
    } else {
      return () => this.invoiceService.getAllElectricityReviewInvoices(this.lastLoadedPageNumber + 1);
    }
  }

  resetInvoicesFilter() {
    this.invoices = [];
    this.invoices = this.loadedInvoices;
  }

  applyStatusFilter() {
    this.expandedRow = null;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  }

  applySelectedFilter() {
    if (this.selectedFilterDropdown === undefined) {
      return;
    }
    this.expandedRow = null;

    const optionIndex = this.selectedFilterDropdown;

    if (optionIndex === 0) {
      this.resetInvoicesFilter();
      if (this.searchTerm.trim() !== '') {
        this.applySearchFilter();
      }
    }
    else if (optionIndex === 1) {
      this.invoices.sort((a, b) => new Date(a.created_at ?? '').getTime() - new Date(b.created_at ?? '').getTime());
    } else if (optionIndex === 2) {
      this.invoices.sort((a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime());
    } else if (optionIndex === 3) {
      this.invoices.sort((a, b) => (a.company?.name ?? '').localeCompare((b.company?.name ?? '')));
    } else if (optionIndex === 4) {
      this.invoices.sort((a, b) => (b.company?.name ?? '').localeCompare((a.company?.name ?? '')));
    } else if (optionIndex === 5) {
      this.invoices.sort((a, b) => {
        const supplierA = a.pods.length > 0 ? a.pods[0].supplier?.name || '' : '';
        const supplierB = b.pods.length > 0 ? b.pods[0].supplier?.name || '' : '';
        return supplierA.localeCompare(supplierB);
      });
    } else if (optionIndex === 6) {
      this.invoices.sort((a, b) => {
        const supplierA = a.pods.length > 0 ? a.pods[0].supplier?.name || '' : '';
        const supplierB = b.pods.length > 0 ? b.pods[0].supplier?.name || '' : '';
        return supplierB.localeCompare(supplierA);
      });
    }
  }

  applySearchFilter() {
    this.expandedRow = null;
    this.resetInvoicesFilter();
    const term = this.searchTerm.trim().toLowerCase();

    if (term === '') {
      this.applySelectedFilter();
    }

    this.invoices = this.invoices.filter(invoice => {
      const timestamp = this.dateConversion.isoToFormattedDateTime(invoice.created_at).toLowerCase();
      const client = (invoice.company?.name || '').toLowerCase();
      const supplier = invoice.pods.length > 0 ? (invoice.pods[0].supplier?.name || '').toLowerCase() : '';

      return timestamp.includes(term) ||
        client.includes(term) ||
        supplier.includes(term);
    });
  }

  reloadInvoices = () => {
    this.loadedInvoices = [];
    this.lastLoadedPageNumber = 0;
    this.loadMoreHidden = false;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  };

  hardReloadInvoices = () => {
    this.expandedRow = null;
    this.loadingExpandedRow = false;
    this.loadedInvoices = [];
    this.lastLoadedPageNumber = 0;
    this.loadMoreHidden = false;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  };

  async getFileUrl(fileId: string) {
    try {
      var response = await lastValueFrom(this.filesService.downloadFile(fileId));
      return response.download_url;

    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    return null;
  }

  async getDisputeBlob(invoiceId: string) {
    try {
      var response = await lastValueFrom(this.invoiceService.getElectricityDispute(invoiceId));
      return response;

    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    return null;
  }

  async downloadFile(fileId: string) {
    const fileUrl = await this.getFileUrl(fileId || '');
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }

  async downloadFileDispute(invoiceId: string) {
    const blob = await this.getDisputeBlob(invoiceId || '');
    const blobUrl = URL.createObjectURL(blob);

    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  }

  updateSingleInvoice = async (invoiceId: string) => {
    try {
      const response = await lastValueFrom(
        this.invoiceService.getElectricityInvoiceById(invoiceId)
      );

      if (response) {
        const loadedIndex = this.loadedInvoices.findIndex(inv => inv.id === invoiceId);
        if (loadedIndex !== -1) {
          Object.assign(this.loadedInvoices[loadedIndex], response);
        }

        const displayIndex = this.invoices.findIndex(inv => inv.id === invoiceId);
        if (displayIndex !== -1) {
          Object.assign(this.invoices[displayIndex], response);

          if (this.expandedRow === displayIndex) {
            await this.reloadLocations(displayIndex);

            const newFileId = response.file.id ?? undefined;
            if (newFileId !== this.expandedFileId) {
              this.expandedFileUrl = await this.getFileUrl(newFileId) ?? undefined;
              this.expandedFileId = newFileId;
            }
          }
        }
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
  };

  reloadCallbackFunction = () => {
    if (this.expandedRow !== null && this.invoices[this.expandedRow]?.id) {
      this.updateSingleInvoice(this.invoices[this.expandedRow].id);
    } else {
      this.reloadInvoices();
    }
  };
  hardReloadCallbackFunction = () => this.hardReloadInvoices();

  createLocationSuccessCallbackFunction = () => this.reloadLocations(this.expandedRow ?? -1);

  async loadLocations(companyId: string) {
    // Load locations
    try {
      const response = await lastValueFrom(this.locationService.getLocationsByCompanyId(companyId));
      return response;
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    return null;
  }

  async reloadLocations(index: number) {
    const locations = await this.loadLocations(this.invoices[index].company?.id ?? '');
    if (locations) {
      this.listOfLocations = locations;
    }
  }

  async updateStatus(invoice: InvoiceElectricityAndCompanyGet, itemIndex: number) {
    if (this.changingStatus) { return; }

    // Reset dialog
    this.confirmDialogTitle = 'admin.finalize_show';
    this.confirmDialogBody = 'admin.finalize_show_desc';

    if (invoice.status === InvoiceStatus.DATA_MISSING) {
      // Check for missing spread
      if (this.isSpreadMissing(invoice) && this.expandedRow != itemIndex) {
        setTimeout(() => {
          this.toggleDetails(itemIndex, true);
          setTimeout(() => {
            this.expandedRowElement?.scroolToSpread();
          }, 600);
          this.cdr.detectChanges();
        });
        return;
      }

      this.toast.showToast(
        'shared.warning',
        'admin.cannot_approve_missing_data',
        'warning',
        5000,
        true
      );
      return;
    }

    // Change dialog text in add spread case
    if (this.isSpreadMissing(invoice) && invoice.visibility_status == VisibilityStatus.UNDER_SILENT_ADMIN_REVIEW) {
      this.confirmDialogTitle = 'admin.finalize_add_spread';
      this.confirmDialogBody = 'admin.finalize_add_spread_desc';
      this.cdr.detectChanges();
    }

    // Open dialog
    var dialogResponse = await this.confirmStatusUpdate.openModal();
    if (!dialogResponse) {
      return;
    }

    const newStatus: ReviewUpdate = {
      visibility_status: VisibilityStatus.READY_FOR_USER
    };

    this.changingStatus = true;

    try {
      const response = await lastValueFrom(this.invoiceService.updateElectricityInvoiceReviewStatus(invoice.id ?? '', newStatus));
      this.hardReloadInvoices();
    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }

    // Log admin which approved invoice
    await this.logFinalizeChanges(invoice);

    // Send event log email to admins
    try {
      const adminChangesSendEmail: AdminChangesSendEmail = {
        invoice_number: invoice.id
      };

      await lastValueFrom(this.invoiceService.sendFileChangesEmail(invoice.file.id, adminChangesSendEmail));
    } catch (error) {
    }

    this.changingStatus = false;
  }

  async logFinalizeChanges(invoice: InvoiceElectricityAndCompanyGet) {
    const message = `Admin ${this.userData.getUser()?.first_name} ${this.userData.getUser()?.last_name} (${this.userData.getUser()?.email}) approved this invoice.`;
    const adminChangesLogData = this.utilsService.getAdminChangesLogData(null, message);

    // Save log
    try {
      const response = await lastValueFrom(this.invoiceService.sendFileChangesLog(invoice.file.id, adminChangesLogData))
    } catch (error) {
    }
  }

  isPendingApproval(invoice: InvoiceElectricityAndCompanyGet): boolean {
    return invoice.status === InvoiceStatus.DONE &&
      invoice.visibility_status === VisibilityStatus.UNDER_ADMIN_REVIEW &&
      !invoice.admin_approved;
  }

  isApproved(invoice: InvoiceElectricityAndCompanyGet): boolean {
    return invoice.admin_approved &&
      invoice.visibility_status === VisibilityStatus.READY_FOR_USER;
  }

  isSpreadMissing(invoice: InvoiceElectricityAndCompanyGet): boolean {
    return this.invoiceHelperService.isSpreadMissing(invoice);
  }

  getButtonText(invoice: InvoiceElectricityAndCompanyGet): string {
    if (this.isApproved(invoice)) {
      return this.translocoService.translate('admin.status.approved');
    }
    else if (this.isPendingApproval(invoice)) {
      return this.translocoService.translate('admin.status.pending_approval');
    }
    else if (this.isSpreadMissing(invoice) && invoice.visibility_status == VisibilityStatus.UNDER_SILENT_ADMIN_REVIEW) {
      return this.translocoService.translate('admin.add_spread');
    }
    return this.translocoService.translate('admin.approve_invoice');
  }

  getButtonBackgroundColor(invoice: InvoiceElectricityAndCompanyGet): string {
    return (this.isPendingApproval(invoice))
      ? 'bg-success-500'
      : 'bg-purple';
  }

  getButtonBorderColor(invoice: InvoiceElectricityAndCompanyGet): string {
    return (this.isPendingApproval(invoice))
      ? 'border-success-500'
      : 'border-purple';
  }

  getButtonHoverColor(invoice: InvoiceElectricityAndCompanyGet): string {
    return this.isPendingApproval(invoice)
      ? 'hover:bg-success-600'
      : 'hover:bg-purple-1200';
  }
}
